import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SettingsCard from '../../organisms/SettingsCard';
import Stepper from '../../organisms/Stepper';
import TableCardGrid from '../../organisms/TableCardGrid';
import TatMinutesProgress from '../../molecules/TatMinutesProgress';
import { useSettingsWizard } from '../../../context/SettingsWizardContext';
import { SETTINGS_STEPS } from '../../../data/settingsSteps';
import { useDispatch, useSelector } from 'react-redux';
import { updateWorkflowSteps } from '../../../redux/Workflow/actions';
import { fetchComplaintTypeById } from '../../../redux/complaintType/actions';
import { formatDurationInMessage, getAllocatedMinutesFromAllLevels, parseDurationToMinutes } from '../../../utils/duration';
// Workflow type dropdown / modals removed (type is now chosen per level in TableCard)
// import Select from '../../atoms/Select';
// import { WorkflowTypeModal, WorkflowChangeModal } from '../../organisms/WorkflowTypeModals';

function unwrapId(value) {
  return value?.data ?? value?.id ?? value;
}

export default function WorkflowStep({
  onNext: _onNext,
  stepMeta,
  stepperProps,
  selectedTableCard,
  roleList = [],
  userList = [],
}) {
  const { state, updateWorkflow: updateWizardWorkflow } = useSettingsWizard();
  const { workflow } = state;

  // Roles already chosen in step 1 (Category Type) are hidden from the Workflow role list
  const categoryRoleIds = state.categoryRoleIds;
  const workflowRoleList = useMemo(() => {
    const blocked = new Set(
      (Array.isArray(categoryRoleIds) ? categoryRoleIds : []).map(String),
    );
    if (!blocked.size) return roleList;
    return roleList.filter((role) => !blocked.has(String(role.id ?? role.value)));
  }, [categoryRoleIds, roleList]);

  // Users whose role was chosen in step 1 are hidden from the Workflow user list
  const workflowUserList = useMemo(() => {
    const blockedIds = new Set(
      (Array.isArray(categoryRoleIds) ? categoryRoleIds : []).map(String),
    );
    if (!blockedIds.size) return userList;
    const norm = (value) => String(value ?? '').trim().toLowerCase();
    const blockedNames = new Set(
      roleList
        .filter((role) => blockedIds.has(String(role.id ?? role.value)))
        .map((role) => norm(role.name ?? role.label)),
    );
    return userList.filter((user) => {
      const roleIds = [
        user?.roleId,
        user?.role?.id,
        ...(Array.isArray(user?.roleIds) ? user.roleIds : []),
        ...(Array.isArray(user?.roles) ? user.roles.map((role) => role?.id ?? role?.roleId ?? role) : []),
      ].filter((value) => value != null).map(String);
      const roleNames = [
        user?.roleName,
        typeof user?.role === 'string' ? user.role : user?.role?.name,
        ...(Array.isArray(user?.roles) ? user.roles.map((role) => role?.name) : []),
      ].filter(Boolean).map(norm);
      return !roleIds.some((id) => blockedIds.has(id)) && !roleNames.some((name) => blockedNames.has(name));
    });
  }, [categoryRoleIds, roleList, userList]);

  const [updatePayload, setUpdatePayload] = useState(null);
  const [saveError, setSaveError] = useState('');

  const handleUpdatePayload = useCallback((payload) => {
    setUpdatePayload(payload);
  }, []);
  const dispatch = useDispatch();

  let { id } = useSelector((state) => state.appDetails || {});
  const selectedType = useSelector((state) => state.complaintType?.selected);
  const typeId = unwrapId(id);
  const selectedTypeId = unwrapId(selectedType?.id ?? selectedType?._id);
  const tatTotal = parseDurationToMinutes(selectedType?.tatMinutes ?? state.tatMinutes);

  useEffect(() => {
    if (typeId == null || typeId === '') return undefined;
    if (selectedTypeId != null && String(selectedTypeId) === String(typeId) && selectedType?.tatMinutes != null) {
      return undefined;
    }
    dispatch(fetchComplaintTypeById(typeId));
    return undefined;
  }, [dispatch, selectedType?.tatMinutes, selectedTypeId, typeId]);

  const usedMinutes = useMemo(() => {
    const workflowLevels = Array.isArray(updatePayload?.tables) ? updatePayload.tables : [];
    return getAllocatedMinutesFromAllLevels(workflowLevels);
  }, [updatePayload]);

  const resolvedSelectedTableCard = selectedTableCard ?? workflow.selectedTableCard ?? null;
  const workflowDescription = Array.isArray(updatePayload?.tables) && updatePayload.tables.length
    ? updatePayload.tables.map((_, index) => `L${index + 1}`).join(' > ')
    : '';

  const onSubmit = async () => {
    setSaveError('');
    if (!updatePayload) {
      setSaveError('Workflow steps are not ready to save.');
      return;
    }

    dispatch(
      updateWorkflowSteps({
        id: updatePayload.id,
        tables: updatePayload,
        onSuccess: (data) => {
          console.log('updateWorkflowSteps onSuccess:', data);
          setSaveError('');
          stepperProps.onNext?.();
        },
        onFail: (error) => {
          const data = error?.response?.data ?? error?.payload ?? error;
          const details = [...new Set(
            Array.isArray(data?.errors)
              ? data.errors.map((item) => item?.message).filter(Boolean)
              : [],
          )];
          const message =
            (details.length ? details.join(' ') : null) ??
            data?.message ??
            data?.error ??
            error?.message ??
            'Failed to update workflow steps';
          const friendly = String(message)
            .replace(/roleIds must contain at least 1 elements?/gi, 'Please select at least one role or user in every level.')
            .replace(/userAssignments must contain at least 1 elements?/gi, 'Please select at least one role or user in every level.');
          setSaveError(formatDurationInMessage(friendly));
        },
      }),
    );
  };

  return (
    <>
      {/* Main wizard stepper */}
      <Stepper
        {...stepperProps}
        steps={SETTINGS_STEPS}
        onNext={onSubmit}
        showNextButton
      />
      {saveError ? (
        <div className="ca-validation-toast" role="alert">
          {saveError}
        </div>
      ) : null}

      <SettingsCard
        title={stepMeta.title}
        description={workflowDescription}
        showNext={false}
        headerExtra={<TatMinutesProgress totalMinutes={tatTotal} usedMinutes={usedMinutes} />}
      >
        {/* Workflow Table Grid */}
        <div className="ca-workflow__main">
          <TableCardGrid
            id={id}
            selectedTableId={resolvedSelectedTableCard?.id}
            onSelect={(table) => updateWizardWorkflow({ selectedTableCard: table })}
            setUpdatePayload={handleUpdatePayload}
            roleList={workflowRoleList}
            userList={workflowUserList}
            onAddBlocked={setSaveError}
            showRoleError={/roleIds|userAssignments|select\s*at\s*least\s*one|role\s*must contain|roles?\s*must contain|users?\s*must/i.test(saveError)}
            showEscalationError={/escalation\s*time|must\s*equal.*TAT/i.test(saveError)}
            prefillSteps={state.prefillWorkflowSteps}
            tatTotalMinutes={tatTotal}
          />
        </div>

        {/* Workflow type modals removed
        <WorkflowTypeModal ... />
        <WorkflowChangeModal ... />
        */}
      </SettingsCard>
    </>
  );
}